// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { OrdersDataViewContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC, useContext, useState } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps;

export interface OrderHistoryOrderNumberAndPaginationContainerStyles {
    tableBodyStyle?: DataTableBodyProps;
    orderStatusLink?: LinkPresentationProps;
}

export const headerStyles: OrderHistoryOrderNumberAndPaginationContainerStyles = {
    tableBodyStyle: {
        css: css`
            tr {
                td:first-child {
                    &::before {
                        content: "";
                        display: inline-block;
                        width: 12px;
                        height: 12px;
                        border-radius: 100%;
                        margin-right: 6px;
                        position: relative;
                        top: 0;
                        background-color: ${getColor("success.main")};
                    }
                }
            }
        `,
    },
    orderStatusLink: {
        typographyProps: {
            weight: "normal",
            size: 14,
            color: "text",
            css: css`
                margin-right: 3px;
            `,
        },
    },
};
const styles = headerStyles;

const OrderHistoryOrderNumberAndPaginationContainer: FC<Props> = ({ id }) => {
    const ordersDataView = useContext(OrdersDataViewContext);
    const [isOpen, setIsOpen] = useState(false);

    if (!ordersDataView || !ordersDataView.value || !ordersDataView.value.length) {
        return null;
    }

    return (
        <StyledWrapper
            css={css`
                margin-bottom: 32px;
            `}
        >
            <StyledWrapper
                css={css`
                    padding: 13px;
                    margin: 32px 0 15px;
                    border: 1px solid ${getColor("common.border")};
                `}
            >
                <GridContainer
                    css={css`
                        > div {
                            justify-content: space-between;
                        }
                    `}
                >
                    <GridItem
                        width={[12, 12, 4, 6, 6]}
                        css={css`
                            > div {
                                margin: auto;
                            }
                        `}
                    >
                        <Zone contentId={id} zoneName="NumberOfOrders" />
                    </GridItem>
                    <GridItem width={[12, 12, 7, 6, 6]}>
                        <Zone contentId={id} zoneName="TopPaginaton" />
                    </GridItem>
                </GridContainer>
            </StyledWrapper>

            <Typography {...styles.orderStatusLink?.typographyProps}>
                <Link {...styles.orderStatusLink} onClick={() => setIsOpen(true)}>
                    {translate("Click Here")}
                </Link>
                {translate("to learn more about your order status")}
            </Typography>
            <Modal sizeVariant="medium" isOpen={isOpen} handleClose={() => setIsOpen(false)} headline="Order Status">
                <Typography>{siteMessage("Custom_OrderStatusDescriptionModal")}</Typography>
                <StyledWrapper
                    css={css`
                        margin: 25px 0;
                    `}
                >
                    <DataTable>
                        <DataTableHead>
                            <DataTableHeader>{translate("Status")}</DataTableHeader>
                            <DataTableHeader>{translate("Description")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.tableBodyStyle}>
                            <DataTableRow>
                                <DataTableCell>{translate("Submitted")} </DataTableCell>
                                <DataTableCell>{siteMessage("Custom_OrderStatusSubmittedDescription")}</DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell
                                    css={css`
                                        &&& {
                                            &::before {
                                                background-color: ${getColor("danger.main")};
                                            }
                                        }
                                    `}
                                >
                                    {translate("Cancelled")}
                                </DataTableCell>
                                <DataTableCell>{siteMessage("Custom_OrderStatusCancelledDescription")}</DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell>{translate("Processing")} </DataTableCell>
                                <DataTableCell>{siteMessage("Custom_OrderStatusProcessingDescription")}</DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell>{translate("Shipped")} </DataTableCell>
                                <DataTableCell>{siteMessage("Custom_OrderStatusShippedDescription")}</DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell
                                    css={css`
                                        &&& {
                                            &::before {
                                                background-color: ${getColor("warning.main")};
                                            }
                                        }
                                    `}
                                >
                                    {translate("Partially Shipped")}
                                </DataTableCell>
                                <DataTableCell>
                                    {siteMessage("Custom_OrderStatusPartiallyShippedDescription")}
                                </DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell>{translate("Allocated")} </DataTableCell>
                                <DataTableCell>{siteMessage("Custom_OrderStatusAllocatedDescription")}</DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell>{translate("Complete")} </DataTableCell>
                                <DataTableCell>{siteMessage("Custom_OrderStatusCompleteDescription")}</DataTableCell>
                            </DataTableRow>
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderHistoryOrderNumberAndPaginationContainer,
    definition: {
        group: "Order History",
        displayName: "OrderNumber And Pagination Container",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
    },
};

export default widgetModule;
